import SwalImport from 'sweetalert2/dist/sweetalert2.js'
import type SwalType from 'sweetalert2'
import type { SweetAlertResult } from 'sweetalert2'

import { sanitize } from '@/utils/sanitize'
import { SANITIZER_PRESETS } from '@/utils/constants/sanitize-config'

import i18n from '@/i18n'

const Swal: typeof SwalType = SwalImport

interface AlertDialogOpts {
  title?: string
  confirmButtonText?: string
  denyButtonText?: string
  cancelButtonText?: string
  hideCancelButton?: boolean
  redConfirmButton?: boolean
  redCancelButton?: boolean
  hintForConfirmString?: string
  checkToConfirm?: boolean
  typeToConfirmString?: string
  caseInsensitiveConfirm?: boolean
  className?: string
  popupClassname?: string
  allowHtml?: boolean
  width?: number
}

interface ConfirmationState {
  text: string
  checked: boolean
}

const alertDialog = (htmlOrText: string, opts: AlertDialogOpts = {}): Promise<SweetAlertResult> => {
  const title = opts?.title ? opts.title : i18n.t('universal.dialog_default_title')

  const clean = (content: string): string =>
    sanitize(content, opts.allowHtml ? SANITIZER_PRESETS.BASE_CONFIG : SANITIZER_PRESETS.STRIP)

  return Swal.fire({
    title: clean(title),
    html: addConfirmationInputs(clean(htmlOrText), opts),
    confirmButtonText: opts?.confirmButtonText ? clean(opts.confirmButtonText) : i18n.t('universal.ok'),
    focusConfirm: false,
    showCancelButton: !opts.hideCancelButton,
    cancelButtonText: opts?.cancelButtonText ? clean(opts.cancelButtonText) : i18n.t('universal.cancel'),
    denyButtonText: opts?.denyButtonText ? clean(opts.denyButtonText) : i18n.t('universal.deny'),
    showDenyButton: !!opts.denyButtonText,
    reverseButtons: true,
    customClass: {
      cancelButton: opts?.redCancelButton ? 'swal2-confirm-danger !font-semibold' : '!font-semibold',
      container: opts?.className,
      confirmButton: opts?.redConfirmButton ? 'swal2-confirm-danger' : 'swal2-confirm-primary',
      popup: opts?.popupClassname,
    },
    width: opts.width,
    showClass: { popup: 'swal2-show', backdrop: 'swal2-show' },
    hideClass: { popup: 'swal2-hide', backdrop: 'swal2-hide' },
    didOpen() {
      initializeConfirmationInputs(opts)
    },
  })
}

const addConfirmationInputs = (html: string, opts: AlertDialogOpts): string => {
  return `
    ${renderTypeToConfirmInput(opts)}
    ${renderConfirmationCheckBox(html, opts)}
  `
}

const renderConfirmationCheckBox = (html: string, opts: AlertDialogOpts): string => {
  if (!opts.checkToConfirm) return html

  return `
    <div class="alert-dialog-content text-sm mt-4">
    <span class="alert-dialog-checkbox">
      <svg width="16" height="16" fill="#fff">
        <path
          d="M10.9 4.8a1 1 0 011.4 0l.1.1c.3.4.3 1 0 1.4L7.8 11a1 1 0 01-1.5 0L3.7 8.2a1 1 0 111.5-1.4l1.9 2 3.8-4z"
        />
      </svg>
      <input
        type="checkbox"
        value=""
        id="check-to-confirm"
      />
    </span>
    <label for="check-to-confirm">${html}</label>
    </div>
  `
}

const renderTypeToConfirmInput = (opts: AlertDialogOpts): string => {
  if (!opts.typeToConfirmString) return ''

  const hint = opts.hintForConfirmString || ''

  return `
    <div class="alert-dialog-text-input">
      <label class="alert-dialog-text-input-label" for="type-to-confirm">${hint}</label>
      <input type="text" value="" id="type-to-confirm" autocomplete="off">
    </div>
  `
}

const initializeConfirmationInputs = (opts: AlertDialogOpts): void => {
  if (!opts.checkToConfirm && !opts.typeToConfirmString) return

  const confirmButton = Swal.getConfirmButton()
  if (confirmButton) {
    confirmButton.setAttribute('disabled', 'disabled')
  }

  const checkbox = window.document.getElementById('check-to-confirm') as HTMLInputElement | null
  const textInput = window.document.getElementById('type-to-confirm') as HTMLInputElement | null
  const state: ConfirmationState = { text: '', checked: false }

  const setButtonState = (): void => {
    const checkedOrNotNeeded = !opts.checkToConfirm || state.checked

    let textConfirmedOrNotNeeded = !opts.typeToConfirmString
    if (opts.typeToConfirmString) {
      if (opts.caseInsensitiveConfirm) {
        textConfirmedOrNotNeeded = state.text.toLowerCase() === opts.typeToConfirmString.toLowerCase()
      } else {
        textConfirmedOrNotNeeded = state.text === opts.typeToConfirmString
      }
    }

    const confirmButton = Swal.getConfirmButton()
    if (!confirmButton) return

    if (checkedOrNotNeeded && textConfirmedOrNotNeeded) {
      confirmButton.removeAttribute('disabled')
    } else {
      confirmButton.setAttribute('disabled', 'disabled')
    }
  }

  checkbox?.addEventListener('change', function () {
    state.checked = checkbox.checked
    setButtonState()
  })

  textInput?.addEventListener('keyup', function (e) {
    state.text = (e.target as HTMLInputElement).value
    setButtonState()
  })
}

export default alertDialog
export type { AlertDialogOpts }
